import type { OrderCalculation } from '../../../types/order-calculation';
import type { TicketOfficeState, TicketOfficeView } from '../../../types/ticket-office';

import type {
    IAdditionalSeatResponse,
    ISeatResponse,
    ISeatsResponse,
    SeatPos,
    Seats, TicketType,
} from '../../../types/types'

import {NumberInputProps} from '../../components/NumberInput';
import { type Allocation, getSeatKey, SeatPicker } from '../../seat_picker';
import { Api } from '../api';
import { updateCalc } from './update-calc';

// Debounce timeout for capacity refresh after cart operations
const CAPACITY_REFRESH_DEBOUNCE_MS = 500;

interface TypeQuantities {
    oldNumbered: number;
    oldUnnumbered: number;
    newNumbered: number;
    newUnnumbered: number;
}

const randomString = (): string => Math.random().toString(16).substring(2);

export const getSeatsByType = (state: TicketOfficeState): Record<string, Seats> => {
    const { ticketTypes, seats } = state;
    const seatsByType: Record<string, Seats> = {};

    for (const type of ticketTypes) {
        seatsByType[type.id] = {
            numbered: [],
            unnumbered: [],
        };
    }

    for (const seat of seats.booked) {
        seatsByType[seat.type].numbered.push(seat);
    }

    for (const seat of seats.additional || []) {
        seatsByType[seat.type].unnumbered.push(seat);
    }

    return seatsByType;
};

const updateSeatsState = async (view: TicketOfficeView): Promise<void> => {
    const { state, update } = view;

    if (!state.eventId) {
        return;
    }

    let url = state.urls.getSeats;
    const params = new URLSearchParams();

    const { memberCard } = state.calc

    if (memberCard) {
        // This query parameter may be removed from the endpoint soon
        params.append('card', memberCard.number)
    }

    if (state.reservationId) {
        params.append('reservation_id', String(state.reservationId));
    }

    const paramStr = params.toString();

    if (paramStr) {
        url += `?${paramStr}`;
    }

    const seats = await Api.getJson<ISeatsResponse>(url);

    if (state.seatUpdaterEnabled) {
        state.seats = seats;
        update();
    }
};

export const getSeats = async (view: TicketOfficeView): Promise<void> => {
    const timeout = view.timeouts.get('update-seats');

    if (!timeout) {
        await updateSeatsState(view);
    }

    // A timeout may have been set while awaiting
    if (timeout) {
        window.clearInterval(timeout);
    }

    const { state } = view;

    if (state.eventId) {
        const newTimeout = window.setInterval(async (): Promise<void> => {
            if (state.seatUpdaterEnabled) {
                await updateSeatsState(view);
            }
        }, 1000 * 30);

        view.timeouts.set('update-seats', newTimeout);
    }
};

export const toggleSeatUpdater = (view: TicketOfficeView, isEnabled: boolean): void => {
    const { state } = view;

    if (state.seatUpdaterEnabled === isEnabled) {
        return;
    }

    // We don't use view.update() here as an immediate render can break input event handling
    state.seatUpdaterEnabled = isEnabled;

    if (!isEnabled) {
        const timeout = view.timeouts.get('update-seats');
        window.clearInterval(timeout);
    } else {
        getSeats(view);
    }
};

// Debounced capacity refresh to update "tickets available" counter after cart changes
const scheduleCapacityRefresh = (view: TicketOfficeView): void => {
    const existingTimeout = view.timeouts.get('capacity-refresh');
    if (existingTimeout) {
        window.clearTimeout(existingTimeout);
    }

    const newTimeout = window.setTimeout(async () => {
        await updateSeatsState(view);
        view.timeouts.delete('capacity-refresh');
    }, CAPACITY_REFRESH_DEBOUNCE_MS);

    view.timeouts.set('capacity-refresh', newTimeout);
};

export const updateSeatData = async (
    view: TicketOfficeView,
    op: 'add' | 'remove',
    seat: ISeatResponse | IAdditionalSeatResponse,
): Promise<string | undefined> => {
    const { state, update } = view;
    const { pendingOps } = state;

    const opId = 'row' in seat ? `update-${seat.row}-${seat.number}` : `update-${seat.uuid}`;

    pendingOps.add(opId);
    update();

    // TODO abort previous request for same seat (or prevent concurrent requests another way)

    const seatUpdaterWasEnabled = state.seatUpdaterEnabled;

    if (seatUpdaterWasEnabled) {
        toggleSeatUpdater(view, false);
    }

    try {
        const response = await $.ajax({
            data: {
                method: op,
                environment: 'admin',
                uuid: 'uuid' in seat ? seat.uuid : undefined,
                row: 'row' in seat ? seat.row : undefined,
                number: 'number' in seat ? seat.number : undefined,
                section_id: 'section_id' in seat ? seat.section_id : undefined,
                type: seat.type,
                price: seat.price,
                sessionId: state.sessionId,
                userId: state.userId,
            },
            dataType: 'json',
            processData: true,
            type: 'POST',
            url: `/api/seats/${state.eventId}/add`,
        }).catch((jqXHR) => {
            // jQuery ajax error - convert to proper error
            const errorMessage = jqXHR.responseJSON?.error || jqXHR.statusText || 'Network request failed';
            throw new Error(errorMessage);
        }) as {
            error?: string;
            uuid?: string;
            calc: OrderCalculation;
        };

        if (response.error) {
            throw new Error(response.error);
        }

        updateCalc(view, response.calc);

        // Schedule capacity refresh to update "tickets available" counter
        scheduleCapacityRefresh(view);

        return response.uuid;
    } finally {
        pendingOps.delete(opId);

        if (seatUpdaterWasEnabled) {
            toggleSeatUpdater(view, true);
        }

        update();
    }
};

export const addNumberedSeat = async (
    view: TicketOfficeView,
    rowNumber: number,
    seatNumber: number,
    ticketTypeId?: number,
    sectionId?: string,
    sectionName?: string,
    customPrice?: number,
): Promise<void> => {
    const { state, update } = view;
    const { lang, ticketTypes, seatTypes, seats } = state;

    const ticketType = ticketTypes.find((type) => {
        if (ticketTypeId) {
            return type.id === ticketTypeId;
        } else {
            const seatTypeId = seatTypes[rowNumber]?.[seatNumber];

            return !seatTypeId || !type.seatTypeIds.length || type.seatTypeIds.includes(seatTypeId);
        }
    });

    if (!ticketType) {
        window.toastr.error(lang.errors.noValidTicketType);

        return;
    }

    const seat: ISeatResponse = {
        row: rowNumber,
        number: seatNumber,
        type: ticketType.id,
        price: customPrice !== undefined ? customPrice.toFixed(2) : ticketType.price,
    };

    if (sectionId !== undefined) {
        seat.section_id = sectionId;
    }
    
    if (sectionName !== undefined) {
        seat.section_name = sectionName;
    }

    seats.booked.push(seat);

    if (!view.state.expandedTicketTypes.has(ticketType.id)) {
        view.state.expandedTicketTypes.add(ticketType.id);
    }

    update();

    try {
        await updateSeatData(view, 'add', seat);
    } catch (error) {
        // On error, remove the seat from the booked seats array
        seats.booked = seats.booked.filter((s) => 
            s.row !== rowNumber || 
            s.number !== seatNumber || 
            (sectionId !== undefined && s.section_id !== sectionId)
        );
        update();
        window.toastr.error(error.message);
    }
};

export const addUnnumberedSeat = async (
    view: TicketOfficeView,
    ticketTypeId: number,
    customPrice?: number,
): Promise<void> => {
    const { state, update } = view;
    const { ticketTypes, seats } = state;
    const ticketType = ticketTypes.find((type) => type.id === ticketTypeId);

    const tempId = randomString();

    const seat: IAdditionalSeatResponse = {
        uuid: '',
        type: ticketTypeId,
        price: customPrice !== undefined ? customPrice.toFixed(2) : ticketType.price,
        tempId,
    };

    if (!seats.additional) {
        seats.additional = [];
    }

    seats.additional.push(seat);
    update();

    try {
        seat.uuid = await updateSeatData(view, 'add', seat);
        delete seat.tempId;
    } catch (error) {
        seats.additional = seats.additional.filter((s) => s !== seat);
    }

    update();
};

export const removeAllSeats = async (
    view: TicketOfficeView,
    seats: Seats,
): Promise<void> => {
    if (seats.numbered.length) {
        seats.numbered.forEach((seat) => {
            removeNumberedSeat(view, seat.row, seat.number)
        })
    }
    if (seats.unnumbered.length) {
        seats.unnumbered.forEach((seat) => {
            removeUnnumberedSeat(view, seat.uuid)
        })
    }
}

export const removeNumberedSeat = async (
    view: TicketOfficeView,
    rowNumber: number,
    seatNumber: number,
    sectionId?: string,
): Promise<void> => {
    const { state, update } = view;
    
    // Find the matching seat to remove, considering section information
    const seatToRemove = state.seats.booked.find((seat) => 
        seat.row === rowNumber && 
        seat.number === seatNumber && 
        (sectionId === undefined || seat.section_id === sectionId)
    );
    
    if (!seatToRemove) {
        console.warn('Seat not found for removal:', rowNumber, seatNumber, sectionId);
        return;
    }

    try {
        // Send the remove request to the server FIRST
        await updateSeatData(view, 'remove', seatToRemove);
        
        // Only update local state after successful server response
        state.seats.booked = state.seats.booked.filter((seat) => 
            seat.row !== rowNumber || 
            seat.number !== seatNumber || 
            (sectionId !== undefined && seat.section_id !== sectionId)
        );
        
        update();
    } catch (error) {
        // Show error to user
        window.toastr.error(state.lang.errors.seatRemovalFailed || 'Failed to remove seat. Please try again.');
        
        // Re-fetch seats to ensure UI is in sync with server
        await updateSeatsState(view);
        
        // Log error for debugging
        console.error('Failed to remove seat:', error);
    }
};

export const removeUnnumberedSeat = async (
    view: TicketOfficeView,
    uuid: string,
): Promise<void> => {
    const { seats } = view.state;
    seats.additional = seats.additional.filter((seat) => seat.uuid !== uuid);
    await updateSeatData(view, 'remove', { uuid, type: 0, price: '' });
};

export const calculateQuantities = (
    seatsByType: Record<string, Seats>,
    change: {
        ticketTypeId: number;
        newQuantity: number;
    },
): Record<string, TypeQuantities> => {
    const quantities: Record<string, TypeQuantities> = {};

    for (const [ticketTypeId, seats] of Object.entries(seatsByType)) {
        quantities[ticketTypeId] = {
            oldNumbered: seats.numbered.length,
            oldUnnumbered: seats.unnumbered.length,
            newNumbered: seats.numbered.length,
            newUnnumbered: seats.unnumbered.length,
        };
    }

    const toChange = quantities[change.ticketTypeId];
    const oldTotal = toChange.oldNumbered + toChange.oldUnnumbered;
    const diff = change.newQuantity - oldTotal;

    if (diff > 0) {
        // When adding, add all to numbered
        toChange.newNumbered += diff;
    } else if (diff < 0) {
        // When removing, remove from unnumbered first and then from numbered

        const totalToRemove = -diff;
        const unnumberedToRemove = Math.min(toChange.oldUnnumbered, totalToRemove);
        const numberedToRemove = totalToRemove - unnumberedToRemove;

        toChange.newUnnumbered -= unnumberedToRemove;
        toChange.newNumbered -= numberedToRemove;
    }

    return quantities;
};

const getUnavailableSeats = (state: TicketOfficeState): SeatPos[] => {
    const { seats: { sold, reserved, disabled } } = state;

    return [...(sold || []), ...(reserved || []), ...disabled].map(
        (seat): SeatPos => ({ rowNumber: seat.row, seatNumber: seat.number }),
    );
};

export const allocateSeats = (
    view: TicketOfficeView,
    newQuantities: Record<string, number>,
): Allocation => {
    const { state } = view;
    const { floorplanData, ticketTypes, seatTypes } = state;

    if (floorplanData) {
        const unavailable = getUnavailableSeats(state);
        const seatPicker = new SeatPicker(floorplanData);

        const canAssignSeat = (seat: SeatPos, ticketTypeId: string): boolean => {
            const ticketType = ticketTypes.find((type) => type.id === Number(ticketTypeId)); // TODO lookup

            if (!ticketType.seatTypeIds.length) {
                return true;
            }

            const seatTypeId = seatTypes[seat.rowNumber][seat.seatNumber];

            return !seatTypeId || ticketType.seatTypeIds.includes(seatTypeId);
        };

        return seatPicker.allocateSeats(newQuantities, unavailable, canAssignSeat);
    } else {
        const allocation: Allocation = {};

        for (const [ticketTypeId, count] of Object.entries(newQuantities)) {
            allocation[ticketTypeId] = {
                numbered: [],
                unnumbered: count,
            };
        }

        return allocation;
    }
};

export const applyAllocation = async (
    view: TicketOfficeView,
    seatsByType: Record<string, Seats>,
    allocation: Allocation,
    customPrice?: number,
): Promise<void> => {
    const numberedToRemove = new Map<string, ISeatResponse>();

    for (const seats of Object.values(seatsByType)) {
        for (const seat of seats.numbered) {
            const key = getSeatKey(seat.row, seat.number);
            numberedToRemove.set(key, seat);
        }
    }

    const promises: Promise<void>[] = [];

    for (const [ticketTypeIdStr, typeAllocation] of Object.entries(allocation)) {
        const ticketTypeId = Number(ticketTypeIdStr);

        for (const seat of typeAllocation.numbered) {
            const key = getSeatKey(seat.rowNumber, seat.seatNumber);
            const existing = numberedToRemove.get(key);

            if (existing) {
                numberedToRemove.delete(key);

                if (existing.type !== ticketTypeId) {
                    promises.push(updateTicketType(view, existing, ticketTypeId));
                }
            } else {
                promises.push(addNumberedSeat(view, seat.rowNumber, seat.seatNumber, ticketTypeId, undefined, undefined, customPrice));
            }
        }
    }

    // Apply additions and updates before removing
    await Promise.all(promises);

    const removePromises: Promise<void>[] = [];

    for (const seat of numberedToRemove.values()) {
        removePromises.push(removeNumberedSeat(view, seat.row, seat.number));
    }

    await Promise.all(removePromises);
};

export const applyUnnumberedChanges = async (
    view: TicketOfficeView,
    seatsByType: Record<string, Seats>,
    quantities: Record<string, TypeQuantities>,
    customPrice?: number,
): Promise<void> => {
    const toAdd: Record<string, number> = {};
    const toRemove: Record<string, number> = {};

    for (const [ticketTypeId, typeQuantities] of Object.entries(quantities)) {
        const { oldUnnumbered, newUnnumbered } = typeQuantities;
        const diff = newUnnumbered - oldUnnumbered;

        if (diff > 0) {
            toAdd[ticketTypeId] = diff;
        } else if (diff < 0) {
            toRemove[ticketTypeId] = -diff;
        }
    }

    const addPromises: Promise<void>[] = [];

    for (const [ticketTypeId, count] of Object.entries(toAdd)) {
        for (let i = 0; i < count; i += 1) {
            addPromises.push(addUnnumberedSeat(view, Number(ticketTypeId), customPrice));
        }
    }

    // Apply additions before removing
    await Promise.all(addPromises);

    const removePromises: Promise<void>[] = [];

    for (const [ticketTypeId, count] of Object.entries(toRemove)) {
        const seats = seatsByType[ticketTypeId].unnumbered
            .filter((seat) => seat.uuid) // Omit seats with only tempId
            .slice(0, count);

        for (const seat of seats) {
            removePromises.push(removeUnnumberedSeat(view, seat.uuid));
        }
    }

    await Promise.all(removePromises);
};

export const toggleSeat = async (
    view: TicketOfficeView,
    rowNumber: number,
    seatNumber: number,
    sectionId?: string,
    sectionName?: string,
): Promise<void> => {
    const { state, update } = view;
    const { pendingOps } = state;

    if (pendingOps.size > 0) {
        return;
    }

    pendingOps.add('update-seats-state');
    update();

    try {
        await updateSeatsState(view);
    } finally {
        pendingOps.delete('update-seats-state');
        update();
    }

    // Check if the seat is already booked, considering section information
    const isBooked = state.seats.booked.some((seat) => 
        seat.row === rowNumber && 
        seat.number === seatNumber && 
        (sectionId === undefined || seat.section_id === sectionId)
    );

    if (isBooked) {
        // Find the matching seat to remove
        const seatToRemove = state.seats.booked.find((seat) => 
            seat.row === rowNumber && 
            seat.number === seatNumber && 
            (sectionId === undefined || seat.section_id === sectionId)
        );
        
        if (seatToRemove) {
            await updateSeatData(view, 'remove', seatToRemove);

            // Manually remove the seat from the booked array to ensure immediate visual update
            state.seats.booked = state.seats.booked.filter((s) =>
                !(s.row === rowNumber && s.number === seatNumber &&
                  (sectionId === undefined || s.section_id === sectionId)));

            // Note: updateSeatData() schedules debounced capacity refresh
            update();
        }
    } else {
        await addNumberedSeat(view, rowNumber, seatNumber, undefined, sectionId, sectionName);
    }
};

const cleanPrice = (price: string): string => {
    const numValue = parseFloat(price);

    return isNaN(numValue) || numValue < 0 ? '0' : price;
};

export const updateTicketType = async (
    view: TicketOfficeView,
    seat: ISeatResponse | IAdditionalSeatResponse,
    newType: number,
): Promise<void> => {
    const { state, update } = view;

    const oldTicketType = state.ticketTypes.find((t) => t.id === seat.type);
    const newTicketType = state.ticketTypes.find((t) => t.id === newType);

    // Preserve custom price if current price differs from old type's default
    const hasCustomPrice = parseFloat(seat.price).toFixed(2) !== parseFloat(oldTicketType.price).toFixed(2);
    const price = hasCustomPrice ? cleanPrice(seat.price) : cleanPrice(newTicketType.price);

    try {
        await updateSeatData(view, 'add', { ...seat, type: newType, price });
        seat.type = newType;
        seat.price = price;
        update();
    } catch (error) {
        window.toastr.error(error.message);
    }
};

export const openTicketModal = (view: TicketOfficeView, ticketType: TicketType, seatsByType: Record<string, Seats>, customPrice?: number) => {
    const { state, update } = view;

    const seats = seatsByType[ticketType.id]

    ticketType.modalCount = seats.numbered.length + seats.unnumbered.length;
    state.summaryItemModalType = 'ticket';
    state.summaryItemModalTicketTypeId = ticketType.id;
    state.summaryItemModalPrice = customPrice;

    update();
}

export const setSeatQuantity = async (
    view: TicketOfficeView,
    type: TicketType,
    seatsByType: Record<string, Seats>,
    newQuantity: number,
    customPrice?: number
): Promise<void> => {
    const { state, update } = view
    toggleSeatUpdater(view, false);

    const quantities = calculateQuantities(seatsByType, { ticketTypeId: type.id, newQuantity });

    const numberedQuantities: Record<string, number> = {};
    let anyNumberedChanges = false;
    state.ticketCountOverrides = {};

    for (const [ticketTypeId, typeQuantities] of Object.entries(quantities)) {
        const { oldNumbered, newNumbered, newUnnumbered } = typeQuantities;

        if (newNumbered) {
            numberedQuantities[ticketTypeId] = newNumbered;
        }

        if (newNumbered !== oldNumbered) {
            anyNumberedChanges = true;
        }

        state.ticketCountOverrides[ticketTypeId] = newNumbered + newUnnumbered;
    }

    update();

    try {
        if (anyNumberedChanges) {
            const allocation = allocateSeats(view, numberedQuantities);

            for (const [ticketTypeId, typeAllocation] of Object.entries(allocation)) {
                if (typeAllocation.unnumbered) {
                    // We were unable to allocate all tickets to numbered seats,
                    // some were allocated to unnumbered.
                    // Update the new quantities accordingly.
                    const typeQuantities = quantities[ticketTypeId];
                    typeQuantities.newUnnumbered += typeAllocation.unnumbered;
                    typeQuantities.newNumbered -= typeAllocation.unnumbered;
                }
            }

            await applyAllocation(view, seatsByType, allocation, customPrice);
        }
    } finally {
        delete state.ticketCountOverrides;
    }

    await applyUnnumberedChanges(view, seatsByType, quantities, customPrice);

    toggleSeatUpdater(view, true);
    update();
};

export const getSeatQuantityInputProps = (
    view: TicketOfficeView,
    seatsByType: Record<string, Seats>,
    type: TicketType,
): NumberInputProps => {
    const { state, update } = view;

    const seats = seatsByType[type.id];
    const totalCount = state.ticketCountOverrides?.[type.id] ?? seats.numbered.length + seats.unnumbered.length;
    const isDisabled = Boolean(state.ticketCountOverrides || state.pendingOps.size > 0);

    const props: NumberInputProps = {
        value: totalCount,
        min: 0,
        isDisabled,
        inputClass: totalCount ? '' : 'number-input__input--faded',
    };

    if (!isDisabled) {
        props.onInput = (): void => {
            toggleSeatUpdater(view, false);
        };

        props.onChange = async (newQuantity: number): Promise<void> => setSeatQuantity(view, type, seatsByType, newQuantity)
    }

    return props;
};
